import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../components/common/ConfirmModal';
import { listSavedSynastries, getSavedSynastryById, deleteSavedSynastry } from '../../services/savedSynastryApi';
import { clearLegacySavedSynastryStorage } from '../../utils/synastryPrivateStorage';
import { localizedServerContent, localizedValue } from '../../localization/presentationLanguage';

const synText = (es, en) => localizedValue({ es, en });

const DETAIL_ROUTE = '/synastry';
const PRIMARY_STORAGE_KEYS = [
  'savedSynastries',
  'synastries:saved',
  'synastry:saved',
  'cosmicLibrary:synastries',
  'zodiacus:cosmic-library',
];

function getSavedResultPayload(entry) {
  return (
    entry?.resultSnapshot ||
    entry?.result ||
    entry?.reading ||
    entry?.analysis ||
    entry?.synastry ||
    entry?.payload ||
    entry?.raw?.resultSnapshot ||
    entry?.raw?.result ||
    entry?.raw?.reading ||
    entry?.raw?.analysis ||
    entry?.raw?.synastry ||
    entry?.raw?.payload ||
    null
  );
}

function inferSavedReadingMode(entry) {
  const explicitMode = String(
    entry?.mode ||
      entry?.readingType ||
      entry?.calculationMode ||
      entry?.sourceType ||
      entry?.raw?.mode ||
      entry?.raw?.readingType ||
      entry?.raw?.calculationMode ||
      entry?.raw?.sourceType ||
      ''
  )
    .trim()
    .toLowerCase();

  if (explicitMode === 'quick' || explicitMode === 'deep') return explicitMode;

  const source = String(
    entry?.source ||
      entry?.sourceType ||
      entry?.raw?.source ||
      entry?.raw?.sourceType ||
      ''
  )
    .trim()
    .toLowerCase();

  if (source.includes('quick') || source.includes('solar')) return 'quick';
  if (source.includes('deep') || source.includes('synastry')) return 'deep';

  const payload = getSavedResultPayload(entry);
  const dims = payload?.compatibility?.dimensions || payload?.dimensions || {};

  const looksQuick = Boolean(
    payload?.pair?.signA && payload?.pair?.signB
  ) || Boolean(
    dims &&
      typeof dims === 'object' &&
      ('energy' in dims || 'expression' in dims || 'magnetism' in dims || 'growth' in dims)
  );

  const looksDeep = Boolean(
    payload?.metadata?.personA ||
      payload?.metadata?.personB ||
      payload?.charts?.personA ||
      payload?.charts?.personB ||
      payload?.houseOverlays ||
      payload?.significantAspects ||
      (dims &&
        typeof dims === 'object' &&
        ('attraction' in dims || 'emotional' in dims || 'communication' in dims || 'karmic' in dims))
  );

  if (looksQuick && !looksDeep) return 'quick';
  if (looksDeep) return 'deep';

  return 'deep';
}

function buildComparisonCandidates(allItems = [], currentItem, mode) {
  const safeItems = Array.isArray(allItems) ? allItems : [];
  const currentId = String(currentItem?.id || '').trim();
  const currentFingerprint = String(currentItem?.raw?.fingerprint || currentItem?.fingerprint || '').trim();

  return safeItems.filter((candidate) => {
    if (!candidate) return false;
    if (inferSavedReadingMode(candidate) !== mode) return false;

    const candidateId = String(candidate.id || '').trim();
    const candidateFingerprint = String(candidate?.raw?.fingerprint || candidate?.fingerprint || '').trim();

    if (currentId && candidateId && currentId === candidateId) return false;
    if (currentFingerprint && candidateFingerprint && currentFingerprint === candidateFingerprint) return false;

    return true;
  });
}

function buildDetailRouteState(item, allItems = []) {
  const mode = inferSavedReadingMode(item);
  const savedCandidates = buildComparisonCandidates(allItems, item, mode);

  if (mode === 'quick') {
    return {
      mode: 'quick',
      initialTab: 'quick',
      reopenSavedSynastry: item.raw,
      reopenSavedReadingMode: 'quick',
      savedSynastryCard: item,
      savedCandidates,
    };
  }

  return {
    openDeepMode: true,
    initialTab: 'deep',
    mode: 'deep',
    reopenSavedSynastry: item.raw,
    reopenSavedReadingMode: 'deep',
    savedSynastryCard: item,
    savedCandidates,
  };
}


function safeJsonParse(value) {
  if (!value || typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function getStorageBuckets() {
  const buckets = [];
  if (typeof window === 'undefined') return buckets;
  if (window.localStorage) buckets.push(window.localStorage);
  return buckets;
}

function extractEntriesFromParsed(parsed) {
  if (!parsed) return [];
  if (Array.isArray(parsed)) return parsed;
  if (typeof parsed !== 'object') return [];

  const candidateArrays = [
    parsed.items,
    parsed.entries,
    parsed.saved,
    parsed.readings,
    parsed.synastries,
    parsed.results,
    parsed.data,
    parsed.list,
  ];

  for (const value of candidateArrays) {
    if (Array.isArray(value)) return value;
  }

  if (parsed.personA || parsed.personB || parsed.result || parsed.reading || parsed.analysis || parsed.synastry || parsed.payload) {
    return [parsed];
  }

  return [];
}

function normalizePercent(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(100, Math.round(num)));
}

function pickFirstNumber(...values) {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return 0;
}

function pickDimensionNumber(...values) {
  for (const value of values) {
    if (value && typeof value === 'object') {
      const nested = pickFirstNumber(value.score, value.value, value.pct, value.percent);
      if (Number.isFinite(nested) && nested !== 0) return nested;
      if (
        value.score === 0 ||
        value.value === 0 ||
        value.pct === 0 ||
        value.percent === 0
      ) {
        return 0;
      }
      continue;
    }

    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return 0;
}

function getNameFromPerson(person, fallback) {
  const signLikeValues = new Set([
    'aries',
    'tauro',
    'taurus',
    'géminis',
    'geminis',
    'cáncer',
    'cancer',
    'leo',
    'virgo',
    'libra',
    'escorpio',
    'scorpio',
    'sagitario',
    'sagittarius',
    'capricornio',
    'capricorn',
    'acuario',
    'aquarius',
    'piscis',
    'pisces',
  ]);

  const candidates = [
    person?.nickname,
    person?.name,
    person?.fullName,
    person?.displayName,
    person?.label,
    person?.personName,
    person?.inputName,
    person?.title,
  ];

  for (const candidate of candidates) {
    const clean = String(candidate || '').trim();
    if (!clean) continue;

    const normalized = clean.toLowerCase();
    if (
      normalized === 'persona a' ||
      normalized === 'persona b' ||
      normalized === 'persona' ||
      normalized === 'person a' ||
      normalized === 'person b'
    ) {
      continue;
    }

    if (signLikeValues.has(normalized)) continue;
    return clean;
  }

  return String(fallback || '').trim();
}


function getSignFromPerson(person) {
  const sign = [
    person?.sunSign,
    person?.sign,
    person?.zodiacSign,
    person?.solarSign,
  ].find((value) => typeof value === 'string' && value.trim());

  return sign ? sign.trim() : '';
}

function getSignCandidate(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function resolveSigns(entry, result, mergedPersonA, mergedPersonB) {
  const signA = [
    getSignFromPerson(mergedPersonA),
    getSignFromPerson(entry?.personA),
    getSignFromPerson(result?.personA),
    getSignFromPerson(result?.people?.personA),
    getSignFromPerson(result?.metadata?.personA),
    getSignFromPerson(result?.charts?.personA),
    getSignCandidate(result?.pair?.signA),
    getSignCandidate(result?.signA),
    getSignCandidate(entry?.pair?.signA),
    getSignCandidate(entry?.signA),
    getSignCandidate(entry?.personASign),
  ].find(Boolean) || '';

  const signB = [
    getSignFromPerson(mergedPersonB),
    getSignFromPerson(entry?.personB),
    getSignFromPerson(result?.personB),
    getSignFromPerson(result?.people?.personB),
    getSignFromPerson(result?.metadata?.personB),
    getSignFromPerson(result?.charts?.personB),
    getSignCandidate(result?.pair?.signB),
    getSignCandidate(result?.signB),
    getSignCandidate(entry?.pair?.signB),
    getSignCandidate(entry?.signB),
    getSignCandidate(entry?.personBSign),
  ].find(Boolean) || '';

  return { signA, signB };
}


function inferAttractionLabel(score) {
  if (score >= 85) return 'muy alta atracción';
  if (score >= 70) return 'alta atracción';
  if (score >= 55) return 'atracción marcada';
  if (score >= 40) return 'atracción media';
  return 'atracción suave';
}

function inferRangeLabel(score) {
  if (score >= 85) return 'compatibilidad muy alta';
  if (score >= 70) return 'compatibilidad alta';
  if (score >= 55) return 'compatibilidad media';
  return 'compatibilidad suave';
}

function inferKarmicLabel(score) {
  if (score >= 80) return 'vínculo kármico';
  if (score >= 65) return 'aprendizaje evolutivo';
  if (score >= 50) return 'conexión significativa';
  return 'conexión en exploración';
}

function getTimestamp(entry) {
  const raw = entry?.savedAt || entry?.createdAt || entry?.updatedAt || entry?.timestamp || entry?.ts;
  if (!raw) return 0;
  const ms = typeof raw === 'number' ? raw : Date.parse(raw);
  return Number.isFinite(ms) ? ms : 0;
}

function normalizeEntry(entry, keyHint, index) {
  const safeEntry = entry?.saved || entry?.item || entry;
  if (!safeEntry || typeof safeEntry !== 'object') return null;

  const personA = safeEntry.personA || safeEntry.a || safeEntry.left || safeEntry.partnerA || safeEntry.inputA || {};
  const personB = safeEntry.personB || safeEntry.b || safeEntry.right || safeEntry.partnerB || safeEntry.inputB || {};
  const result =
    safeEntry.resultSnapshot ||
    safeEntry.result ||
    safeEntry.reading ||
    safeEntry.analysis ||
    safeEntry.synastry ||
    safeEntry.payload ||
    safeEntry.raw?.resultSnapshot ||
    safeEntry.raw?.result ||
    safeEntry.raw?.reading ||
    safeEntry.raw?.analysis ||
    safeEntry.raw?.synastry ||
    safeEntry.raw?.payload ||
    {};
  const dims =
    result?.compatibility?.dimensions ||
    result?.dimensions ||
    result?.dimensionScores ||
    result?.metrics ||
    safeEntry?.compatibility?.dimensions ||
    safeEntry?.dimensions ||
    safeEntry?.raw?.compatibility?.dimensions ||
    safeEntry?.raw?.dimensions ||
    {};
  const metaPersonA = result?.metadata?.personA || {};
  const metaPersonB = result?.metadata?.personB || {};

  const compatibility = normalizePercent(
    pickFirstNumber(
      result?.compatibility?.masterScore,
      result?.compatibility?.master,
      result?.compatibility,
      result?.compatibilityScore,
      result?.score,
      safeEntry?.compatibility?.masterScore,
      safeEntry?.compatibility?.master,
      safeEntry?.compatibility,
      safeEntry?.compatibilityScore,
      safeEntry?.score,
      safeEntry?.raw?.compatibility?.masterScore,
      safeEntry?.raw?.compatibility?.master,
      safeEntry?.raw?.compatibility,
      safeEntry?.raw?.compatibilityScore,
      safeEntry?.raw?.score
    )
  );

  const attraction = normalizePercent(
    pickDimensionNumber(
      dims?.attraction,
      dims?.magnetism,
      dims?.chemistry,
      result?.attraction,
      result?.magnetism,
      result?.chemistry,
      safeEntry?.attraction,
      safeEntry?.magnetism,
      safeEntry?.chemistry,
      safeEntry?.raw?.attraction,
      safeEntry?.raw?.magnetism,
      safeEntry?.raw?.chemistry,
      compatibility
    )
  );

  const karmic = normalizePercent(
    pickDimensionNumber(
      dims?.karmic,
      dims?.growth,
      dims?.karmicAxis,
      dims?.karma,
      result?.karmic,
      result?.growth,
      result?.karmicAxis,
      result?.karma,
      safeEntry?.karmic,
      safeEntry?.growth,
      safeEntry?.karmicAxis,
      safeEntry?.karma,
      safeEntry?.raw?.karmic,
      safeEntry?.raw?.growth,
      safeEntry?.raw?.karmicAxis,
      safeEntry?.raw?.karma,
      compatibility
    )
  );

  const id = String(
    safeEntry.id ||
    safeEntry._id ||
    safeEntry.fingerprint ||
    result.id ||
    (typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${keyHint || 'syn'}-${getTimestamp(safeEntry) || Date.now()}-${index}-${Math.random()
          .toString(36)
          .slice(2)}`)
  );

  const mergedPersonA = { ...metaPersonA, ...personA };
  const mergedPersonB = { ...metaPersonB, ...personB };

  const fallbackNameA =
    getNameFromPerson(safeEntry?.personA, '') ||
    getNameFromPerson(result?.personA, '') ||
    getNameFromPerson(result?.people?.personA, '') ||
    getNameFromPerson(result?.metadata?.personA, '') ||
    getNameFromPerson(result?.charts?.personA, '') ||
    'Persona A';

  const fallbackNameB =
    getNameFromPerson(safeEntry?.personB, '') ||
    getNameFromPerson(result?.personB, '') ||
    getNameFromPerson(result?.people?.personB, '') ||
    getNameFromPerson(result?.metadata?.personB, '') ||
    getNameFromPerson(result?.charts?.personB, '') ||
    'Persona B';

  const nameA = getNameFromPerson(mergedPersonA, fallbackNameA);
  const nameB = getNameFromPerson(mergedPersonB, fallbackNameB);
  const { signA, signB } = resolveSigns(safeEntry, result, mergedPersonA, mergedPersonB);

  const titleLeft = nameA;
  const titleRight = nameB;
  const plainTitle = [titleLeft, titleRight]
    .filter(Boolean)
    .filter((value) => {
      const normalized = String(value || '').trim().toLowerCase();
      return normalized !== 'persona a' && normalized !== 'persona b';
    })
    .join(' ✦ ');
  const subtitle = [signA, signB].filter(Boolean).join(' ✦ ');
  const rawSummary = [
    result.summary,
    result.overview,
    result.shortSummary,
    safeEntry.summary,
    safeEntry.overview,
  ].find((value) => typeof value === 'string' && value.trim()) || '';

  const mode = inferSavedReadingMode(safeEntry);
  const summary = mode === 'deep' ? '' : rawSummary;

  return {
    id,
    keyHint,
    raw: safeEntry,
    title: plainTitle || [signA, signB].filter(Boolean).join(' ✦ ') || 'Lectura guardada',
    subtitle: plainTitle ? subtitle : '',
    personA: { ...mergedPersonA, name: nameA, sign: signA },
    personB: { ...mergedPersonB, name: nameB, sign: signB },
    compatibility,
    attraction,
    karmic,
    rangeLabel: inferRangeLabel(compatibility),
    attractionLabel: inferAttractionLabel(attraction),
    karmicLabel: inferKarmicLabel(karmic),
    summary,
    savedAt: getTimestamp(safeEntry),
    createdAt: safeEntry?.createdAt || result?.createdAt || null,
    updatedAt: safeEntry?.updatedAt || result?.updatedAt || null,
    mode,
    sourceType: safeEntry?.sourceType || result?.sourceType || safeEntry?.source || result?.source || '',
  };
}

function readAllSavedSynastries() {
  clearLegacySavedSynastryStorage();
  const buckets = getStorageBuckets();
  if (!buckets.length) return [];

  const items = [];
  const seen = new Set();

  function pushCandidate(entry, keyHint) {
    const normalized = normalizeEntry(entry, keyHint, items.length);
    if (!normalized) return;
    if (seen.has(normalized.id)) return;
    seen.add(normalized.id);
    items.push(normalized);
  }

  const local = typeof window !== 'undefined' ? window.localStorage : null;

  if (local) {
    PRIMARY_STORAGE_KEYS.forEach((key) => {
      const parsed = safeJsonParse(local.getItem(key));
      const entries = extractEntriesFromParsed(parsed);
      if (entries.length) {
        entries.forEach((entry) => pushCandidate(entry, key));
      }
    });
  }

  return items.sort((a, b) => b.savedAt - a.savedAt);
}

async function mapWithConcurrency(items, limit, mapper) {
  const safeItems = Array.isArray(items) ? items : [];
  const safeLimit = Math.max(1, Number(limit || 1));
  const results = new Array(safeItems.length);
  let cursor = 0;

  async function worker() {
    while (cursor < safeItems.length) {
      const currentIndex = cursor;
      cursor += 1;
      results[currentIndex] = await mapper(safeItems[currentIndex], currentIndex);
    }
  }

  const workers = Array.from({ length: Math.min(safeLimit, safeItems.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

async function readRemoteSavedSynastries() {
  try {
    const response = await listSavedSynastries(50);
    const remoteItems = Array.isArray(response?.items) ? response.items : [];

    const hydrated = await mapWithConcurrency(remoteItems, 6, async (item, index) => {
      try {
        const detail = await getSavedSynastryById(item.id);
        const saved = detail?.item || detail?.saved || null;

        return normalizeEntry(
          {
            ...(saved || item),
            id: item.id,
            _id: item.id,
            title: saved?.title || item.title,
            mode: saved?.mode || item.mode,
            sourceType: saved?.sourceType || item.sourceType,
            compatibilityScore: saved?.compatibilityScore ?? item.compatibilityScore,
            personA: saved?.personA || item.personA,
            personB: saved?.personB || item.personB,
            resultSnapshot: saved?.resultSnapshot || item.resultSnapshot,
            result: saved?.resultSnapshot || item.result,
            reading: saved?.resultSnapshot || item.reading,
            tags: saved?.tags || item.tags,
            createdAt: saved?.createdAt || item.createdAt,
            updatedAt: saved?.updatedAt || item.updatedAt,
            fingerprint: saved?.fingerprint || item.fingerprint,
          },
          'remote-saved-synastries',
          index
        );
      } catch {
        return normalizeEntry(
          {
            ...item,
            id: item.id,
            _id: item.id,
          },
          'remote-saved-synastries-fallback',
          index
        );
      }
    });

    return { ok: true, items: hydrated.filter(Boolean) };
  } catch {
    return { ok: false, items: [] };
  }
}


function normalizeSearchText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove accents
    .toLowerCase()
    .trim();
}

function formatSavedAt(timestamp) {
  if (!timestamp) return 'Guardado recientemente';
  try {
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(timestamp));
  } catch {
    return 'Guardado recientemente';
  }
}

function SavedCard({ item, onOpen, onDelete, deleting }) {
  const [hover, setHover] = React.useState(false);
  return (
    <article
      className="zodiacus-saved-card"
      style={{
        ...styles.card,
        ...(hover ? styles.cardHover : {}),
        alignContent: 'start',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={styles.cardGlow} aria-hidden />
      <div style={styles.cardSparkles} aria-hidden />

      <div
        style={{
          ...styles.cardTopRow,
          ...styles.cardTopRowStack,
        }}
        className="zodiacus-saved-card-top-row"
      >
        <div style={styles.cardTitleWrap}>
          <div style={styles.cardTopMeta}>
            <div style={styles.cardEyebrow}>{synText('💞 Biblioteca Cósmica', '💞 Cosmic Library')}</div>
            <h3 style={styles.cardTitle} title={item.title}>{item.title}</h3>
            {item.subtitle ? (
              <div style={styles.cardSubtitle} title={item.subtitle}>
                {item.subtitle}
              </div>
            ) : null}
          </div>
        </div>
        <div
          style={{
            ...styles.scoreOrbWrap,
            ...styles.scoreOrbWrapStack,
          }}
          className="zodiacus-saved-score-orb-wrap"
        >
          <div
            style={{
              ...styles.scoreOrb,
              ...(hover ? styles.scoreOrbHover : {}),
            }}
          >
            {item.compatibility}%
          </div>
          <div style={styles.scoreOrbLabelInline}>{item.rangeLabel}</div>
        </div>
      </div>

      <div style={styles.cardMidRow} className="zodiacus-saved-card-mid-row">
        <div style={styles.badgeColumn} className="zodiacus-saved-badge-column">
          <span style={styles.badgeGold}>{item.attractionLabel}</span>
          <span style={styles.badgePurple}>{item.karmicLabel}</span>
          <span style={styles.badgeSoft}>{item.compatibility >= 75 ? synText('alta compatibilidad', 'high compatibility') : synText('lectura guardada', 'saved reading')}</span>
        </div>

        <div style={styles.metricGrid}>
          <div style={styles.metricBox} className="zodiacus-saved-metric-box">
            <div style={styles.metricLabel}>{synText('Compatibilidad', 'Compatibility')}</div>
            <div style={styles.metricValue}>{item.compatibility}%</div>
          </div>
          <div style={styles.metricBox} className="zodiacus-saved-metric-box">
            <div style={styles.metricLabel}>{synText('Atracción', 'Attraction')}</div>
            <div style={styles.metricValue}>{item.attraction}%</div>
          </div>
          <div style={styles.metricBox} className="zodiacus-saved-metric-box">
            <div style={styles.metricLabel}>{synText('Kármico', 'Karmic')}</div>
            <div style={styles.metricValue}>{item.karmic}%</div>
          </div>

          <div style={styles.metricChartBox} className="zodiacus-saved-metric-chart-box">
            <svg width="70" height="60" viewBox="0 0 100 80">
              {(() => {
                const compat = item.compatibility;
                const attract = item.attraction;
                const karmic = item.karmic;

                const barWidth = (v) => Math.max(4, v * 0.7);

                return (
                  <>
                    {/* labels */}
                    <text x="2" y="18" fontSize="6" fill="rgba(255,255,255,0.6)">C</text>
                    <text x="2" y="38" fontSize="6" fill="rgba(255,255,255,0.6)">A</text>
                    <text x="2" y="58" fontSize="6" fill="rgba(255,255,255,0.6)">K</text>

                    {/* background bars */}
                    <rect x="10" y="12" width="70" height="8" rx="4" fill="rgba(255,255,255,0.08)" />
                    <rect x="10" y="32" width="70" height="8" rx="4" fill="rgba(255,255,255,0.08)" />
                    <rect x="10" y="52" width="70" height="8" rx="4" fill="rgba(255,255,255,0.08)" />

                    {/* values */}
                    <rect x="10" y="12" width={barWidth(compat)} height="8" rx="4" fill="#DAA520" />
                    <rect x="10" y="32" width={barWidth(attract)} height="8" rx="4" fill="#8A5CFF" />
                    <rect x="10" y="52" width={barWidth(karmic)} height="8" rx="4" fill="#C9A7FF" />
                  </>
                );
              })()}
            </svg>
          </div>
        </div>
      </div>

      {item.summary ? <p style={styles.cardSummary}>{localizedServerContent(item.summary, 'Saved relationship reading with compatibility, attraction, and growth highlights.')}</p> : null}

      <div style={styles.cardFooter}>
        <div style={styles.savedAt}>✦ {formatSavedAt(item.savedAt)}</div>
        <div style={styles.cardFooterActions} className="zodiacus-saved-card-footer-actions">
          <button type="button" onClick={() => onDelete(item)} style={styles.deleteButton} disabled={deleting}>
            {deleting ? synText('Eliminando…', 'Deleting…') : synText('Eliminar', 'Delete')}
          </button>
          <button type="button" onClick={() => onOpen(item)} style={styles.openButton}>
            {synText('Ver lectura', 'View reading')}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function SavedSynastriesScreen() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);
  const [screenError, setScreenError] = useState('');
  const refreshSeqRef = useRef(0);

    useEffect(() => {
    let cancelled = false;

    async function refreshItems() {
      const requestId = refreshSeqRef.current + 1;
      refreshSeqRef.current = requestId;

      const localItems = readAllSavedSynastries();
      const remoteResult = await readRemoteSavedSynastries();
      const nextItems = remoteResult.ok ? remoteResult.items : localItems;

      if (!cancelled && refreshSeqRef.current === requestId) {
        setItems(nextItems);
      }
    }

    refreshItems();
    window.addEventListener('storage', refreshItems);
    window.addEventListener('focus', refreshItems);

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') refreshItems();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      window.removeEventListener('storage', refreshItems);
      window.removeEventListener('focus', refreshItems);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const filteredItems = useMemo(() => {
    const q = normalizeSearchText(query);
    if (!q) return items;

    return items.filter((item) => {
      const searchableFields = [
        item.title,
        item.subtitle,
        item.summary,
        item.rangeLabel,
        item.attractionLabel,
        item.karmicLabel,
        item.personA?.name,
        item.personA?.sign,
        item.personB?.name,
        item.personB?.sign,
        String(item.compatibility || ''),
        String(item.attraction || ''),
        String(item.karmic || ''),
      ];

      const haystack = normalizeSearchText(
        searchableFields
          .filter(Boolean)
          .join(' ')
      );

      return haystack.includes(q);
    });
  }, [items, query]);

  function handleBack() {
    // Read the last active synastry tab from sessionStorage.
    // QuickModeTab / DeepModeTab should store it as 'synastry:mode'.
    const lastMode =
      typeof window !== 'undefined'
        ? window.sessionStorage.getItem('synastry:mode')
        : null;

    if (lastMode === 'deep') {
      navigate('/synastry', {
        state: { openDeepMode: true, initialTab: 'deep', mode: 'deep' },
      });
      return;
    }

    // default quick
    navigate('/synastry', {
      state: { mode: 'quick', initialTab: 'quick' },
    });
  }

  function handleOpen(item) {
    navigate(DETAIL_ROUTE, {
      state: buildDetailRouteState(item, items),
    });
  }

  function handleDelete(item) {
    if (!item) return;
    setScreenError('');
    setPendingDeleteItem(item);
  }

  function handleCancelDelete() {
    setScreenError('');
    setPendingDeleteItem(null);
  }

  async function handleConfirmDelete() {
    if (!pendingDeleteItem) return;

    const item = pendingDeleteItem;
    setScreenError('');
    setDeletingId(item.id);

    try {
      if (item?.id) {
        await deleteSavedSynastry(item.id);
      }

      const remoteResult = await readRemoteSavedSynastries();
      if (remoteResult.ok) {
        setItems(remoteResult.items);
      } else {
        setItems((prev) => prev.filter((entry) => entry.id !== item.id));
      }
    } catch (err) {
      setScreenError(err?.message || 'No se pudo eliminar la lectura guardada.');
    } finally {
      setDeletingId('');
      setPendingDeleteItem(null);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.bgAuraTop} aria-hidden />
      <div style={styles.bgAuraBottom} aria-hidden />
      <div style={styles.bgNebula} aria-hidden />

      <div style={styles.shell}>
        <header style={styles.hero}>
          <button
  type="button"
  onClick={handleBack}
  style={styles.backButton}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-1px)';
    e.currentTarget.style.boxShadow = '0 16px 34px rgba(230,193,94,0.18), 0 0 18px rgba(230,193,94,0.16), inset 0 1px 0 rgba(255,255,255,0.10)';
    e.currentTarget.style.borderColor = 'rgba(230,193,94,0.46)';
    e.currentTarget.style.filter = 'brightness(1.04)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = '0 10px 24px rgba(230,193,94,0.10), inset 0 1px 0 rgba(255,255,255,0.08)';
    e.currentTarget.style.borderColor = 'rgba(230,193,94,0.30)';
    e.currentTarget.style.filter = 'none';
  }}
>
  {synText('← Volver', '← Back')}
</button>

          <div style={styles.heroPanel}>
            <div style={styles.eyebrow}>{synText('✦ Biblioteca de Sinastrías Guardadas', '✦ Saved Synastry Library')}</div>
            <h1 style={styles.title}>{synText('Biblioteca Cósmica', 'Cosmic Library')}</h1>
            <p style={styles.subtitle}>
              {synText('Revisitá vínculos, conservá tu historial emocional y volvé a abrir cada lectura premium sin recalcularla.', 'Revisit connections, keep your relationship history, and reopen each premium reading without recalculating it.')}
            </p>

            <div style={styles.heroStatsRow}>
              <div style={styles.heroStat}>
                <span style={styles.heroStatValue}>{items.length}</span>
                <span style={styles.heroStatLabel}>{synText('lecturas', 'readings')}</span>
              </div>
              <div style={styles.heroStat}>
                <span style={styles.heroStatValue}>
                  {items.length ? `${Math.round(items.reduce((acc, item) => acc + item.compatibility, 0) / items.length)}%` : '0%'}
                </span>
                <span style={styles.heroStatLabel}>{synText('promedio', 'average')}</span>
              </div>
              <div style={styles.heroStat}>
                <span style={styles.heroStatValue}>
                  {items.filter((item) => item.karmic >= 70).length}
                </span>
                <span style={styles.heroStatLabel}>{synText('kármicas', 'karmic')}</span>
              </div>
            </div>
          </div>
        </header>

        <section style={styles.toolbar}>
          <label htmlFor="synastry-search" style={{ display: 'none' }}>
            {synText('Buscar sinastrías', 'Search synastries')}
          </label>
          <input
            id="synastry-search"
            name="synastrySearch"
            className="zodiacus-saved-search"
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={synText('Buscar por nombre, signo o vibe cósmica', 'Search by name, sign, or cosmic vibe')}
            style={styles.searchInput}
          />
          <div style={styles.toolbarNote}>Gold · Violet · Mobile first</div>
        </section>

        {screenError ? (
          <section style={styles.errorState}>
            <div style={styles.errorIcon}>⚠</div>
            <div style={styles.errorContent}>
              <div style={styles.errorTitle}>{synText('No se pudo completar la acción', 'The action could not be completed')}</div>
              <div style={styles.errorText}>{localizedServerContent(screenError, 'Please try again.')}</div>
            </div>
          </section>
        ) : null}

        {filteredItems.length ? (
          <section style={styles.grid}>
            {filteredItems.map((item) => (
              <SavedCard
                key={item.id}
                item={item}
                onOpen={handleOpen}
                onDelete={handleDelete}
                deleting={deletingId === item.id}
              />
            ))}
          </section>
        ) : (
          <section style={styles.emptyState}>
            <div style={styles.emptyIcon}>✨</div>
            <h2 style={styles.emptyTitle}>{synText('Todavía no hay lecturas guardadas', 'There are no saved readings yet')}</h2>
            <p style={styles.emptyText}>
              {synText('Guardá una sinastría desde Deep Mode y va a aparecer acá con su compatibilidad, badges y acceso rápido.', 'Save a synastry from Deep Mode and it will appear here with its compatibility, badges, and quick access.')}
            </p>
            <button
              type="button"
              onClick={() =>
                navigate(DETAIL_ROUTE, {
                  state: { openDeepMode: true, initialTab: 'deep', mode: 'deep' },
                })
              }
              style={styles.emptyButton}
            >
              {synText('Ir a sinastría profunda', 'Go to deep synastry')}
            </button>
          </section>
        )}
      </div>

      <ConfirmModal
        open={Boolean(pendingDeleteItem)}
        title="Eliminar lectura"
        message="¿Querés eliminar esta sinastría guardada de tu Biblioteca Cósmica? Esta acción no se puede deshacer."
        confirmLabel="Eliminar lectura"
        cancelLabel="Cancelar"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}

const styles = {
  page: {
    position: 'relative',
    minHeight: '100vh',
    color: '#F8F4E9',
    overflow: 'hidden',
    background:
      'radial-gradient(circle at 20% 18%, rgba(120,70,255,0.25) 0%, rgba(120,70,255,0.0) 28%), ' +
      'radial-gradient(circle at 82% 12%, rgba(90,40,200,0.35) 0%, rgba(90,40,200,0.0) 36%), ' +
      'radial-gradient(circle at 50% 70%, rgba(140,90,255,0.20) 0%, rgba(140,90,255,0.0) 40%), ' +
      'linear-gradient(180deg, #2B004F 0%, #3A0066 22%, #4B0082 45%, #5A1AA6 65%, #4B0082 82%, #2B004F 100%)',
    animation: 'starDrift 140s linear infinite',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 34px)',
  },
  cardTitleWrap: {
  minWidth: 0,
  width: '100%',
},
  bgAuraTop: {
    position: 'absolute',
    top: -180,
    right: -120,
    width: 360,
    height: 360,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(218,165,32,0.22) 0%, rgba(218,165,32,0) 72%)',
    filter: 'blur(6px)',
    pointerEvents: 'none',
  },
  bgAuraBottom: {
    position: 'absolute',
    left: -120,
    bottom: -180,
    width: 420,
    height: 420,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(132,68,255,0.18) 0%, rgba(132,68,255,0) 72%)',
    filter: 'blur(10px)',
    pointerEvents: 'none',
  },
  bgNebula: {
    position: 'absolute',
    inset: 0,
    background:
      'radial-gradient(circle at 30% 40%, rgba(132,68,255,0.18), transparent 45%), radial-gradient(circle at 70% 60%, rgba(218,165,32,0.14), transparent 50%)',
    filter: 'blur(60px)',
    opacity: 0.6,
    pointerEvents: 'none',
    animation: 'nebulaFloat 40s ease-in-out infinite alternate',
  },
  shell: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: 1220,
    margin: '0 auto',
    padding: '20px 16px 56px',
    boxSizing: 'border-box',
  },
  hero: {
  display: 'grid',
  gap: 16,
  marginBottom: 18,
  position: 'relative',
  zIndex: 8,
  marginTop: 10,
  paddingTop: 4,
},
  backButton: {
  alignSelf: 'flex-start',
  padding: '0 14px',
  borderRadius: 999,
  border: '1px solid rgba(230,193,94,0.30)',
  background: 'linear-gradient(120deg, rgba(230,193,94,0.16), rgba(255,255,255,0.05), rgba(230,193,94,0.16))',
  backgroundSize: '200% 100%',
  color: '#E6C15E',
  fontSize: 13,
  fontWeight: 800,
  cursor: 'pointer',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  width: '100%',
  position: 'relative',
  zIndex: 12,
  minHeight: 54,
  paddingTop: 14,
  paddingBottom: 14,
  marginTop: 8,
  touchAction: 'manipulation',
  WebkitTapHighlightColor: 'transparent',
  boxShadow: '0 10px 24px rgba(230,193,94,0.10), inset 0 1px 0 rgba(255,255,255,0.08)',
  transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease, filter .18s ease',
  animation: 'buttonShimmer 8s linear infinite',
},
  heroPanel: {
    position: 'relative',
    padding: '20px 18px',
    borderRadius: 24,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.05))',
    boxShadow: '0 24px 60px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.10)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
  },
  eyebrow: {
    color: '#E8C56B',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  title: {
    margin: 0,
    fontSize: 'clamp(28px, 5vw, 46px)',
    lineHeight: 1.04,
    fontWeight: 900,
    letterSpacing: -0.04 + 'em',
  },
  subtitle: {
    margin: '10px 0 0',
    maxWidth: 760,
    color: 'rgba(248,244,233,0.82)',
    fontSize: 15,
    lineHeight: 1.65,
  },
  heroStatsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 10,
    marginTop: 18,
  },
  heroStat: {
    padding: '14px 12px',
    borderRadius: 18,
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(10, 8, 22, 0.50)',
    display: 'grid',
    gap: 6,
    justifyItems: 'start',
  },
  heroStatValue: {
    color: '#F6D77A',
    fontSize: 20,
    fontWeight: 900,
    lineHeight: 1,
  },
  heroStatLabel: {
    color: 'rgba(248,244,233,0.72)',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.9,
  },
  toolbar: {
    display: 'grid',
    gap: 10,
    marginBottom: 18,
  },
  searchInput: {
    width: '100%',
    minHeight: 48,
    boxSizing: 'border-box',
    borderRadius: 16,
    padding: '0 16px',
    border: '1px solid rgba(255,255,255,0.10)',
    background: 'rgba(255,255,255,0.06)',
    color: '#F8F4E9',
    outline: 'none',
    fontSize: 14,
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
  },
  toolbarNote: {
    color: 'rgba(248,244,233,0.62)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  errorState: {
    marginBottom: 18,
    padding: '14px 16px',
    borderRadius: 18,
    border: '1px solid rgba(255,120,120,0.26)',
    background: 'linear-gradient(180deg, rgba(255,90,90,0.12), rgba(120,20,20,0.10))',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    boxShadow: '0 16px 34px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  errorIcon: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(255,255,255,0.08)',
    color: '#FFD9D9',
    fontSize: 16,
    flex: '0 0 auto',
  },
  errorContent: {
    display: 'grid',
    gap: 4,
  },
  errorTitle: {
    color: '#FFE5E5',
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  errorText: {
    color: 'rgba(255,235,235,0.86)',
    fontSize: 13,
    lineHeight: 1.55,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 420px))',
    gap: 14,
    alignItems: 'start',
    justifyContent: 'start',
  },
  card: {
    position: 'relative',
    display: 'grid',
    gap: 12,
    padding: '18px 18px 16px',
   minHeight: 'unset',
height: 'auto',
    borderRadius: 24,
    border: '1px solid rgba(218,165,32,0.55)',
    background:
      'linear-gradient(180deg, rgba(255,255,255,0.12), rgba(255,255,255,0.05))',
    boxShadow:
      '0 22px 56px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.12)',
    backdropFilter: 'blur(22px)',
    WebkitBackdropFilter: 'blur(22px)',
    overflow: 'hidden',
    transition: 'transform .25s ease, box-shadow .25s ease, border .25s ease',
    animation: 'cosmicReveal .6s ease both',
  },
  cardHover: {
    transform: 'translateY(-6px) scale(1.02)',
    boxShadow:
      '0 40px 100px rgba(0,0,0,0.6), 0 0 40px rgba(132,68,255,0.45), 0 0 20px rgba(218,165,32,0.45)',
    border: '1px solid rgba(218,165,32,0.85)',
  },
  cardGlow: {
    position: 'absolute',
    inset: 'auto -60px -80px auto',
    width: 240,
    height: 240,
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(132,68,255,0.30) 0%, rgba(132,68,255,0.0) 70%)',
    filter: 'blur(18px)',
    pointerEvents: 'none',
  },
  cardSparkles: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage:
      'radial-gradient(2px 2px at 20% 30%, rgba(255,215,120,0.9), transparent), radial-gradient(2px 2px at 70% 40%, rgba(255,215,120,0.8), transparent), radial-gradient(1px 1px at 50% 75%, rgba(255,235,180,0.9), transparent), radial-gradient(1.5px 1.5px at 80% 70%, rgba(255,215,120,0.8), transparent)',
    opacity: 0.35,
    animation: 'sparkleFloat 6s ease-in-out infinite',
  },
  cardTopRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    alignItems: 'start',
    gap: 10,
  },
  cardTopRowStack: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
  cardEyebrow: {
    color: '#E6C15E',
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 0.95,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  cardTitle: {
    margin: 0,
    fontSize: 18,
    lineHeight: 1.1,
    fontWeight: 900,
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip',
  },
  cardSubtitle: {
    marginTop: 4,
    color: 'rgba(248,244,233,0.66)',
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip',
  },
  scoreOrbWrap: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
    alignSelf: 'start',
    flexWrap: 'nowrap',
  },
  scoreOrbWrapStack: {
    justifyContent: 'flex-start',
    marginTop: 4,
  },
  cardTopMeta: {
    display: 'grid',
    gap: 2,
  },
  scoreOrb: {
    width: 62,
    height: 62,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background:
      'radial-gradient(circle at 30% 30%, rgba(255,235,170,0.55), rgba(218,165,32,0.32) 38%, rgba(90,35,180,0.30) 100%)',
    border: '1px solid rgba(255,255,255,0.18)',
    color: '#FFF4CF',
    fontWeight: 900,
    fontSize: 18,
    letterSpacing: '.02em',
    boxShadow:
      '0 12px 28px rgba(218,165,32,0.24), 0 0 18px rgba(218,165,32,0.14)',
    animation: 'orbPulse 3.2s ease-in-out infinite',
  },
  scoreOrbHover: {
    boxShadow:
      '0 20px 40px rgba(218,165,32,0.55), 0 0 40px rgba(218,165,32,0.35)',
    transform: 'scale(1.08)',
  },
  scoreOrbLabel: {
    color: 'rgba(248,244,233,0.62)',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.7,
    textTransform: 'uppercase',
  },
  scoreOrbLabelInline: {
    color: 'rgba(248,244,233,0.78)',
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
  },
  cardMidRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(150px, auto) minmax(0, 1fr)',
    gap: 12,
    minHeight: 'unset',
    alignItems: 'start',
    marginTop: -4,
  },
  badgeColumn: {
    display: 'grid',
    gap: 6,
    alignItems: 'start',
    justifyItems: 'start',
    alignContent: 'start',
    marginTop: 30,
  },
  badgeGold: {
    padding: '7px 12px',
    borderRadius: 999,
    background: 'rgba(218,165,32,0.18)',
    border: '1px solid rgba(218,165,32,0.34)',
    color: '#F5DE9C',
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    boxShadow: '0 0 10px rgba(218,165,32,0.25)',
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 30,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
  },
  badgePurple: {
    padding: '7px 12px',
    borderRadius: 999,
    background: 'rgba(132,68,255,0.20)',
    border: '1px solid rgba(132,68,255,0.35)',
    color: '#E2D6FF',
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    boxShadow: '0 0 10px rgba(132,68,255,0.30)',
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 30,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
  },
  badgeSoft: {
    padding: '7px 12px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.10)',
    color: 'rgba(248,244,233,0.82)',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.55,
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 30,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
  },
  metricGrid: {
    display: 'grid',
    height: 'auto',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 8,
    width: '100%',
    alignSelf: 'start',
    marginTop: -2,
  },
  metricBox: {
    padding: '9px 8px',
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.10)',
    background:
      'linear-gradient(180deg, rgba(12,8,24,0.65), rgba(8,6,16,0.65))',
    display: 'grid',
    gap: 3,
    minWidth: 0,
    minHeight: 70,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
    alignContent: 'center',
  },
  metricChartBox: {
    padding: '7px 7px',
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.10)',
    background:
      'linear-gradient(180deg, rgba(12,8,24,0.65), rgba(8,6,16,0.65))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 70,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
  },
  metricLabel: {
    color: 'rgba(248,244,233,0.62)',
    fontSize: 8,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.35,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'clip',
    lineHeight: 1,
  },
  metricValue: {
    color: '#F8F4E9',
    fontSize: 17,
    fontWeight: 900,
    lineHeight: 1,
    textAlign: 'center',
  },
  cardSummary: {
    margin: 0,
    color: 'rgba(248,244,233,0.78)',
    fontSize: 14,
    lineHeight: 1.65,
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginTop: 12,
    flexWrap: 'wrap',
  },
  cardFooterActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    width: '100%',
  },
  savedAt: {
    color: 'rgba(248,244,233,0.62)',
    fontSize: 11,
    fontWeight: 700,
  },
  deleteButton: {
    minHeight: 40,
    padding: '0 14px',
    borderRadius: 999,
    border: '1px solid rgba(255,120,120,0.22)',
    background: 'rgba(255,90,90,0.10)',
    color: '#FFD7D7',
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.7,
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  openButton: {
    minHeight: 42,
    padding: '0 16px',
    borderRadius: 999,
    border: '1px solid rgba(218,165,32,0.35)',
    background:
      'linear-gradient(120deg, rgba(218,165,32,0.35), rgba(132,68,255,0.22), rgba(218,165,32,0.35))',
    backgroundSize: '200% 100%',
    animation: 'buttonShimmer 6s linear infinite',
    color: '#FFF4CF',
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    cursor: 'pointer',
    boxShadow:
      '0 14px 36px rgba(218,165,32,0.22), inset 0 1px 0 rgba(255,255,255,0.15)',
    transition: 'transform .15s ease, box-shadow .15s ease',
  },
  emptyState: {
    padding: '38px 20px',
    borderRadius: 28,
    border: '1px solid rgba(255,255,255,0.10)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.04))',
    boxShadow: '0 20px 48px rgba(0,0,0,0.28)',
    textAlign: 'center',
    display: 'grid',
    gap: 12,
    justifyItems: 'center',
  },
  emptyIcon: {
    width: 68,
    height: 68,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(255,255,255,0.07)',
    fontSize: 28,
  },
  emptyTitle: {
    margin: 0,
    fontSize: 24,
    fontWeight: 900,
  },
  emptyText: {
    margin: 0,
    maxWidth: 620,
    color: 'rgba(248,244,233,0.74)',
    fontSize: 15,
    lineHeight: 1.7,
  },
  emptyButton: {
    minHeight: 44,
    padding: '0 16px',
    borderRadius: 999,
    border: '1px solid rgba(218,165,32,0.26)',
    background: 'linear-gradient(180deg, rgba(218,165,32,0.20), rgba(132,68,255,0.14))',
    color: '#FFF2C2',
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
  },
};

const cosmicStyle = document.createElement('style');
cosmicStyle.innerHTML = `
@keyframes cosmicReveal {
  0% { opacity:0; transform:translateY(12px) scale(.96); }
  100% { opacity:1; transform:translateY(0) scale(1); }
}

@keyframes orbPulse {
  0% { box-shadow:0 0 10px rgba(218,165,32,0.25); }
  50% { box-shadow:0 0 28px rgba(218,165,32,0.45); }
  100% { box-shadow:0 0 10px rgba(218,165,32,0.25); }
}

@keyframes starDrift {
  0% {
    background-position:
      0px 0px,
      0px 0px,
      0px 0px,
      0px 0px,
      0px 0px,
      0px 0px;
  }

  100% {
    background-position:
      400px 300px,
      -300px 200px,
      200px -400px,
      -250px 150px,
      320px -260px,
      -180px 340px;
  }
}

@keyframes buttonShimmer {
  0% { background-position: 0% 0%; }
  100% { background-position: 200% 0%; }
}

@keyframes nebulaFloat {
  0% { transform: translate3d(0px,0px,0px) scale(1); }
  50% { transform: translate3d(-40px,20px,0px) scale(1.05); }
  100% { transform: translate3d(30px,-30px,0px) scale(1.08); }
}

@keyframes sparkleFloat {
  0% { transform: translateY(0px) scale(1); opacity:0.25 }
  50% { transform: translateY(-6px) scale(1.05); opacity:0.6 }
  100% { transform: translateY(0px) scale(1); opacity:0.25 }
}

/* Cosmic Library search placeholder visibility */
.zodiacus-saved-search::placeholder {
  color: #E6C15E;
  opacity: 0.9;
}

.zodiacus-saved-search::-webkit-input-placeholder { color: #E6C15E; }
.zodiacus-saved-search::-moz-placeholder { color: #E6C15E; }
.zodiacus-saved-search:-ms-input-placeholder { color: #E6C15E; }

@media (max-width: 560px) {
  /* Compact premium mobile sizing */
  .zodiacus-saved-card {
    padding: 12px 12px 10px !important;
    gap: 7px !important;
    border-radius: 20px !important;
  }

  .zodiacus-saved-card-top-row h3 {
    font-size: 15px !important;
    line-height: 1.05 !important;
  }

  .zodiacus-saved-card-top-row {
    gap: 8px !important;
  }

  .zodiacus-saved-card-top-row .zodiacus-saved-score-orb-wrap {
    margin-top: 2px !important;
    gap: 8px !important;
  }

  .zodiacus-saved-score-orb-wrap > div:first-child {
    width: 54px !important;
    height: 54px !important;
    font-size: 16px !important;
  }

  .zodiacus-saved-score-orb-wrap > div:last-child {
    font-size: 10px !important;
    letter-spacing: 0.55px !important;
  }

  .zodiacus-saved-badge-column {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 6px !important;
    margin-top: 2px !important;
  }

  .zodiacus-saved-badge-column > span {
    min-height: 26px !important;
    padding: 5px 10px !important;
    font-size: 10px !important;
    letter-spacing: 0.45px !important;
  }

  .zodiacus-saved-card-mid-row {
    gap: 8px !important;
    margin-top: 0 !important;
  }

  .zodiacus-saved-card .zodiacus-saved-score-orb-wrap div {
    transform: none !important;
  }

  .zodiacus-saved-card .zodiacus-saved-card-mid-row {
    grid-template-columns: minmax(0, 1fr) !important;
  }

  .zodiacus-saved-card .zodiacus-saved-badge-column {
    justify-content: flex-start !important;
  }

  .zodiacus-saved-card .zodiacus-saved-card-mid-row > div:last-child {
    width: 100% !important;
  }

  .zodiacus-saved-card .zodiacus-saved-card-mid-row > div:last-child {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 6px !important;
  }

  .zodiacus-saved-metric-box {
    min-height: 52px !important;
    padding: 6px 5px !important;
    border-radius: 14px !important;
    gap: 2px !important;
  }

  .zodiacus-saved-metric-box > div:first-child {
    font-size: 7px !important;
    letter-spacing: 0.3px !important;
  }

  .zodiacus-saved-metric-box > div:last-child {
    font-size: 14px !important;
  }

  .zodiacus-saved-metric-chart-box {
    display: none !important;
  }

  .zodiacus-saved-card p {
    font-size: 12px !important;
  }

  .zodiacus-saved-card .zodiacus-saved-card-footer-actions {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 8px !important;
    width: 100% !important;
  }

  .zodiacus-saved-card .zodiacus-saved-card-footer-actions button {
    width: 100% !important;
    min-height: 34px !important;
    font-size: 10px !important;
    letter-spacing: 0.55px !important;
    padding: 0 10px !important;
  }

  .zodiacus-saved-card button {
    min-height: 34px !important;
  }

  .zodiacus-saved-card .savedAt,
  .zodiacus-saved-card [style*="savedAt"] {
    font-size: 10px !important;
  }
}
`;
if (typeof document !== 'undefined' && !document.getElementById('zodiacus-cosmic-anim')) {
  cosmicStyle.id = 'zodiacus-cosmic-anim';
  document.head.appendChild(cosmicStyle);
}
